import { driver, type Config, type DriveStep, type Driver, type PopoverDOM } from 'driver.js'
import 'driver.js/dist/driver.css'
import type { DialogServiceMethods } from 'primevue/dialogservice'
import { showConsoleTourCompletion } from '@/components/tour/tour-completion-dialog'
import {
  bodyForVariant,
  stepsForVariant,
  titleForVariant,
  type TourStepContent,
  type TourVariant
} from '@/components/tour/console-tour-steps'
import { useConsoleTourStore } from '@/stores/consoleTour'
import '@/components/tour/console-tour.css'

/**
 * Drives the console orientation tour with driver.js.
 *
 * Nothing else in the console imports driver.js -- if the library is ever swapped out, this file
 * and the stylesheet beside it are the whole blast radius. Callers hand over content (from
 * `console-tour-steps.ts`) and get back start/stop.
 */

/** How long the sidebar pin transition takes before nav labels are readable (see Full.vue). */
const SIDEBAR_SETTLE_MS = 350

function selectorFor (step: TourStepContent): string {
  return `[data-tour="${step.target}"]`
}

/**
 * Steps whose anchor is actually on the page right now.
 *
 * Navigation is filtered by permissions and enabled features (see NavSidebar's `menuGroups`), so
 * a step's target may simply not exist for this person. driver.js can skip missing elements on
 * its own, but then the progress counter promises more steps than it delivers -- filtering up
 * front keeps "Step 3 of 5" honest.
 */
function presentSteps (steps: TourStepContent[]): TourStepContent[] {
  return steps.filter(step => !!document.querySelector(selectorFor(step)))
}

/**
 * Exactly the steps this person will be walked through: their variant's steps, minus any whose
 * anchor is not on the page. The invitation quotes this rather than a number written into the
 * copy, so "5 steps" cannot drift from what the tour then does.
 */
export function resolveTourSteps (variant: TourVariant): TourStepContent[] {
  return presentSteps(stepsForVariant(variant))
}

function delay (ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms))
}

export interface StartTourOptions {
  source: 'auto' | 'help_menu'
  /** Called when the person reaches the end. Not called when they skip out. */
  onCompleted?: () => void
  /** Called when the tour stops for any reason, after onCompleted. */
  onStopped?: () => void
}

export function useConsoleTour () {
  const tourStore = useConsoleTourStore()
  let activeDriver: Driver | null = null

  /**
   * Adds the things driver.js has no config for: a text "Skip tour" escape hatch next to the
   * progress counter (an X in the corner is easy to miss, and a tour you cannot obviously leave
   * is a trap), and the optional "Learn more" link that keeps step bodies short.
   */
  function decoratePopover (popover: PopoverDOM, step: TourStepContent, onSkip: () => void) {
    const skipButton = document.createElement('button')
    skipButton.type = 'button'
    skipButton.className = 'console-tour-skip'
    skipButton.textContent = 'Skip tour'
    skipButton.addEventListener('click', onSkip)
    popover.progress.insertAdjacentElement('afterend', skipButton)

    if (step.learnMoreUrl) {
      const link = document.createElement('a')
      link.className = 'console-tour-learn-more'
      link.href = step.learnMoreUrl
      link.target = '_blank'
      link.rel = 'noopener'
      link.textContent = 'Learn more'
      popover.description.insertAdjacentElement('afterend', link)
    }
  }

  function buildSteps (content: TourStepContent[], variant: TourVariant, onSkip: () => void): DriveStep[] {
    return content.map((step, index) => ({
      element: selectorFor(step),
      popover: {
        title: titleForVariant(step, variant),
        description: bodyForVariant(step, variant),
        side: step.side ?? 'bottom',
        align: step.align ?? 'start',
        onPopoverRender: (popover: PopoverDOM) => decoratePopover(popover, step, onSkip)
      },
      onHighlighted: () => tourStore.markStepViewed(index, step.id)
    }))
  }

  function stop () {
    if (activeDriver) {
      const instance = activeDriver
      // Null first: destroy() fires onDestroyStarted, and the handler checks this to tell an
      // intentional teardown apart from the person pressing Escape.
      activeDriver = null
      instance.destroy()
    }
    tourStore.markStopped()
  }

  /**
   * Starts the tour, pinning the sidebar for its duration so nav labels are readable -- collapsed,
   * the sidebar is 72px of icons and the labels are `opacity-0`, which makes "your old menu is
   * here" a step about nothing. Full.vue restores the person's own pin setting when `isRunning`
   * goes back to false.
   *
   * Resolves once the tour has started, not once it has finished.
   */
  async function start (options: StartTourOptions): Promise<boolean> {
    if (activeDriver) {
      return false
    }

    const variant = tourStore.variant
    tourStore.markStarted(options.source)

    // Let the sidebar finish expanding before measuring anything.
    await delay(SIDEBAR_SETTLE_MS)

    const content = resolveTourSteps(variant)
    if (content.length === 0) {
      // Nothing to point at -- a permission set this narrow has no console to tour.
      tourStore.markStopped()
      options.onStopped?.()
      return false
    }

    let finishing = false

    const handleSkip = () => {
      if (finishing) {
        return
      }
      finishing = true

      const index = activeDriver?.getActiveIndex() ?? 0
      void tourStore.markSkipped(index, content[index]?.id ?? 'unknown')
      stop()
      options.onStopped?.()
    }

    const handleDone = () => {
      if (finishing) {
        return
      }
      finishing = true

      void tourStore.markCompleted()
      stop()
      options.onCompleted?.()
      options.onStopped?.()
    }

    const config: Config = {
      steps: buildSteps(content, variant, handleSkip),
      showProgress: true,
      progressText: 'Step {{current}} of {{total}}',
      nextBtnText: 'Next',
      prevBtnText: 'Back',
      doneBtnText: 'Finish',
      popoverClass: 'console-tour-popover',
      stagePadding: 6,
      stageRadius: 10,
      smoothScroll: true,
      // Clicking a highlighted nav link mid-tour would navigate away and strand the remaining
      // steps. Escape and "Skip tour" are still there, so nobody is stuck.
      disableActiveInteraction: true,
      allowClose: true,
      overlayClickBehavior: 'close',
      overlayOpacity: 0.65,
      onCloseClick: handleSkip,
      onDoneClick: handleDone,
      // Fires for Escape and overlay clicks too. Defining it means driver.js hands teardown to
      // us, so every exit path lands in exactly one of these handlers.
      onDestroyStarted: () => {
        if (!activeDriver) {
          return
        }
        handleSkip()
      }
    }

    activeDriver = driver(config)
    activeDriver.drive()

    return true
  }

  return { start, stop }
}

/**
 * The tour plus the card that closes it out. Both entry points -- the automatic offer and the
 * Help menu -- want exactly this, so neither has to remember that finishing the last step is
 * supposed to lead somewhere.
 */
export function useConsoleTourRunner (dialog: DialogServiceMethods) {
  const { start, stop } = useConsoleTour()

  function run (source: StartTourOptions['source']) {
    return start({
      source,
      onCompleted: () => showConsoleTourCompletion(dialog)
    })
  }

  return { run, stop }
}
